import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import Highlight from "../components/Highlight";
import Loading from "../components/Loading";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";

// Helper function to get or set initial profile data from localStorage
const getInitialProfile = (user) => {
  const savedProfile = localStorage.getItem("userProfile");
  const initialProfile = {
    name: user.name || "",
    email: user.email || "",
    nickname: user.nickname || "",
    phone: user.phone_number || "",
    shippingAddress: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
  };
  if (savedProfile) {
    const parsedProfile = JSON.parse(savedProfile);
    return {
      ...initialProfile,
      ...parsedProfile,
      shippingAddress: {
        ...initialProfile.shippingAddress,
        ...parsedProfile.shippingAddress,
      },
    };
  }
  return initialProfile;
};

// Function to check if MFA is present in ID token
const isMFAVerified = (idToken) => {
  try {
    const decoded = jwtDecode(idToken);
    const amr = decoded.amr;
    return Array.isArray(amr) && amr.includes("mfa");
  } catch (error) {
    console.error("Error decoding ID token:", error);
    return false;
  }
};

// Function to trigger step-up authentication
const triggerStepUpAuth = (loginWithRedirect, config) => {
  loginWithRedirect({
    authorizationParams: {
      acr_values: "http://schemas.openid.net/pape/policies/2007/06/multi-factor",
      redirect_uri: window.location.origin,
      scope: "openid profile email",
      // "ext-branding": "",
    },
  });
};

// Function to authenticate a secondary user and get their ID token
const authenticateSecondaryUser = async (loginWithPopup) => {
  try {
    const result = await loginWithPopup({
      max_age: 0,
      scope: "openid profile email",
      connection: undefined, // Let the user pick the connection (e.g., Google)
    });
    return await result.getIdTokenClaims();
  } catch (err) {
    console.error("Authentication failed:", err);
    throw err;
  }
};

export const ProfileComponent = ({ config }) => {
  const { user, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect, loginWithPopup } = useAuth0();
  const [accessToken, setAccessToken] = useState("");
  const [decodedToken, setDecodedToken] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState(getInitialProfile(user));
  const [activeTab, setActiveTab] = useState("General");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [error, setError] = useState("");

  const clientId = config.clientId;
  const domain = config.domain;
  const redirectUri = window.location.origin;

  useEffect(() => {
    const fetchAccessToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAccessToken(token);
        const decoded = jwtDecode(token);
        setDecodedToken(decoded);
      } catch (error) {
        console.error("Error fetching access token:", error);
      }
    };

    const checkMFA = async () => {
      const idTokenClaims = await getIdTokenClaims();
      if (idTokenClaims && !isMFAVerified(idTokenClaims.__raw)) {
        setMfaRequired(true);
      }
    };

    fetchAccessToken();
    checkMFA();
  }, [getAccessTokenSilently, getIdTokenClaims]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name in profile.shippingAddress) {
      setProfile((prev) => ({
        ...prev,
        shippingAddress: { ...prev.shippingAddress, [name]: value },
      }));
    } else {
      setProfile((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Save profile updates to localStorage
  const handleSave = async () => {
    if (activeTab === "Shipping Address" && mfaRequired) {
      triggerStepUpAuth(loginWithRedirect, config);
      return;
    }
    setIsEditing(false);
    localStorage.setItem("userProfile", JSON.stringify(profile));
    console.log("Profile saved to localStorage:", profile);
  };

  // Custom edit handler to check MFA before enabling edit mode for shipping address
  const handleEdit = async () => {
    if (activeTab === "Shipping Address") {
      const idTokenClaims = await getIdTokenClaims();
      if (idTokenClaims && !isMFAVerified(idTokenClaims.__raw)) {
        setMfaRequired(true);
        triggerStepUpAuth(loginWithRedirect, config);
      } else {
        setIsEditing(true);
      }
    } else {
      setIsEditing(true);
    }
  };

  // Handle account linking with Management API audience
  const handleLinkAccount = async () => {
    try {
      setError("");
      const accessToken = await getAccessTokenSilently({
        audience: config.managementApiAudience, // Request Management API audience
        scope: "read:users update:users",
        ignoreCache: true,
      });
      const { sub } = user;
      const { __raw: targetUserIdToken, email_verified, email } = await authenticateSecondaryUser(loginWithPopup);

      if (!email_verified) {
        throw new Error(
          `Account linking is only allowed to a verified account. Please verify your email ${email}.`
        );
      }

      const response = await fetch(`https://${config.domain}/api/v2/users/${sub}/identities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          link_with: targetUserIdToken,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to link account: ${errorText}`);
      }

      // Refresh user data after linking
      const idTokenClaims = await getIdTokenClaims();
      setMfaRequired(!isMFAVerified(idTokenClaims.__raw));
    } catch (error) {
      setError(error.message);
    }
  };

  // Handle account unlinking with Management API audience
  const handleUnlink = async (provider, userId) => {
    try {
      setError("");
      const accessToken = await getAccessTokenSilently({
        audience: config.managementApiAudience, // Request Management API audience
        scope: "update:users",
      });
      const { sub } = user;
      const response = await fetch(
        `https://${config.domain}/api/v2/users/${sub}/identities/${provider}/${userId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to unlink account: ${errorText}`);
      }

      // Refresh user data after unlinking
      const idTokenClaims = await getIdTokenClaims();
      setMfaRequired(!isMFAVerified(idTokenClaims.__raw));
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* User Profile Card */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">User Profile</h2>
        <p className="text-gray-500 mb-6">Info about you and your preferences</p>

        {/* Sidebar and Main Content */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className="w-full md:w-1/5 bg-gray-50 p-4 rounded-lg">
            <ul className="space-y-2">
              <li
                className={`cursor-pointer ${activeTab === "General" ? "text-gray-700 font-medium" : "text-gray-500"}`}
                onClick={() => setActiveTab("General")}
              >
                General
              </li>
              <li
                className={`cursor-pointer ${activeTab === "Shipping Address" ? "text-gray-700 font-medium" : "text-gray-500"}`}
                onClick={() => setActiveTab("Shipping Address")}
              >
                Shipping Address
              </li>
              <li
                className={`cursor-pointer ${activeTab === "Security" ? "text-gray-700 font-medium" : "text-gray-500"}`}
                onClick={() => setActiveTab("Security")}
              >
                Security
              </li>
              <li
                className={`cursor-pointer ${activeTab === "Sessions" ? "text-gray-700 font-medium" : "text-gray-500"}`}
                onClick={() => setActiveTab("Sessions")}
              >
                Sessions
              </li>
              <li
                className={`cursor-pointer ${activeTab === "Linked Accounts" ? "text-gray-700 font-medium" : "text-gray-500"}`}
                onClick={() => setActiveTab("Linked Accounts")}
              >
                Linked Accounts
              </li>
            </ul>
          </div>

          {/* Main Content */}
          <div className="w-full md:w-4/5 bg-gray-50 p-6 rounded-lg">
            {/* General Section */}
            {activeTab === "General" && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4">General</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={profile.name}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-gray-900 bg-white disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={profile.email}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-gray-900 bg-white disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nickname</label>
                    <input
                      type="text"
                      name="nickname"
                      value={profile.nickname}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-gray-900 bg-white disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={profile.phone}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-gray-900 bg-white disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Shipping Address Section */}
            {activeTab === "Shipping Address" && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4">Shipping Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Street</label>
                    <input
                      type="text"
                      name="street"
                      value={profile.shippingAddress.street}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-gray-900 bg-white disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input
                      type="text"
                      name="city"
                      value={profile.shippingAddress.city}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-gray-900 bg-white disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <input
                      type="text"
                      name="state"
                      value={profile.shippingAddress.state}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-gray-900 bg-white disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={profile.shippingAddress.postalCode}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-gray-900 bg-white disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <input
                      type="text"
                      name="country"
                      value={profile.shippingAddress.country}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-gray-900 bg-white disabled:bg-gray-100"
                    />
                  </div>
                </div>
                {mfaRequired && (
                  <p className="text-red-600 mt-4">
                    
                  </p>
                )}
              </div>
            )}

            {/* Linked Accounts Section */}
            {activeTab === "Linked Accounts" && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4">Linked Accounts</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Connection</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Is Social</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile Data</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {user && user["https://auth0.com/identities"] && user["https://auth0.com/identities"].map((identity, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">{identity.connection}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{identity.isSocial ? "true" : "false"}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{identity.provider}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{identity.user_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <pre className="text-sm text-gray-500">{JSON.stringify(identity.profileData, null, 2)}</pre>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleUnlink(identity.provider, identity.user_id)}
                              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                            >
                              Unlink
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4">
                  <label htmlFor="provider-select" className="block text-sm font-medium text-gray-700">
                    Select Provider to Link:
                  </label>
                  <select
                    id="provider-select"
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  >
                    <option value="">Select a provider</option>
                    <option value="google-oauth2">Google</option>
                    <option value="facebook">Facebook</option>
                    <option value="microsoft">Microsoft</option>
                    {/* Add more providers as configured in your Auth0 tenant */}
                  </select>
                  <button
                    onClick={handleLinkAccount}
                    disabled={!selectedProvider}
                    className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Link Account
                  </button>
                </div>
                {error && <p className="text-red-600 mt-2">{error}</p>}
              </div>
            )}

            {/* Edit/Save Button (visible for General and Shipping Address) */}
            {(activeTab === "General" || activeTab === "Shipping Address") && (
              <div className="text-right mb-6">
                {isEditing ? (
                  <button
                    onClick={handleSave}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                ) : (
                  <button
                    onClick={handleEdit}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tokens Card */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Tokens</h2>

        {/* ID Token Section */}
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-800 mb-2">ID Token</h4>
          <Highlight language="json">{JSON.stringify(user, null, 2)}</Highlight>
        </div>

        {/* Access Token Section */}
        <div>
          <h4 className="text-lg font-medium text-gray-800 mb-2">Access Token</h4>
          {decodedToken ? (
            <Highlight language="json">{JSON.stringify(decodedToken, null, 2)}</Highlight>
          ) : (
            <p className="text-gray-600">Loading token claims...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default withAuthenticationRequired(ProfileComponent, {
  onRedirecting: () => <Loading />,
});